import { useState, useEffect } from "react";
import { KILL_REASONS } from "../../constants/dealStages";
import { loadCompetition, upsertCompetition, updateDeal } from "../../supabase";

// ─── KillDealModal ──────────────────────────────────────────────────────────
// Modal that opens whenever a deal moves TO a terminal stage (Passed / Lost
// / Dead). Captures:
//   • killed_reason (dropdown filtered by stage)
//   • killed_narrative (textarea)
//   • deal_competition detail row (bidders, our/winning bids, winner, debrief)
//     available on every terminal stage — Passed and Dead deals may not have
//     had US competing, but capturing "who won" or "why the deal never went"
//     is still useful for post-mortem analysis. Every field is optional so
//     the section can be skipped when it's genuinely irrelevant.
//
// The parent decides the target stage and passes it in via `targetStage`.
// This component owns the entire round-trip:
//   1. updateDeal({ stage, killed_reason, killed_narrative, killed_at })
//   2. If any competition field is filled OR a row already exists, upsert
//      into deal_competition.
//
// Cancel = keep the deal on its previous stage (nothing persists).
// Save   = ship both writes, then call onSaved({updated}) so the parent
//          can swap in the fresh deal.

const EMPTY_COMP = {
  num_bidders_estimated: "",
  our_final_bid_psf: "",
  our_final_bid_total: "",
  made_best_and_final: null,      // tri-state: null (unknown) / true / false
  winning_bid_psf: "",
  winning_bid_total: "",
  winner_name: "",
  broker_debrief_notes: "",
};

export default function KillDealModal({ open, deal, targetStage, onSaved, onCancel, userName = null, darkMode = true }) {
  const [reason, setReason] = useState("");
  const [narrative, setNarrative] = useState("");
  const [comp, setComp] = useState(EMPTY_COMP);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const reasonOptions = KILL_REASONS[targetStage] || [];
  const [compExisted, setCompExisted] = useState(false);

  // Prefill from any existing kill/competition data when the modal opens.
  // Competition prefill runs on every terminal stage now — the section is
  // optional but always available for later editing.
  useEffect(() => {
    if (!open || !deal) return;
    setError(null);
    setReason(deal.killedReason || "");
    setNarrative(deal.killedNarrative || "");
    loadCompetition(deal.id).then(c => {
      if (c) {
        setCompExisted(true);
        setComp({
          num_bidders_estimated: c.num_bidders_estimated ?? "",
          our_final_bid_psf:     c.our_final_bid_psf     ?? "",
          our_final_bid_total:   c.our_final_bid_total   ?? "",
          made_best_and_final:   c.made_best_and_final,
          winning_bid_psf:       c.winning_bid_psf       ?? "",
          winning_bid_total:     c.winning_bid_total     ?? "",
          winner_name:           c.winner_name           ?? "",
          broker_debrief_notes:  c.broker_debrief_notes  ?? "",
        });
      } else {
        setCompExisted(false);
        setComp(EMPTY_COMP);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, deal?.id, targetStage]);

  // Skip writing the deal_competition row when the section is entirely blank
  // AND no row already exists. If a row exists, always write so the user can
  // clear values by blanking them.
  function competitionHasContent() {
    return (
      comp.num_bidders_estimated !== "" ||
      comp.our_final_bid_psf     !== "" ||
      comp.our_final_bid_total   !== "" ||
      comp.made_best_and_final   !== null ||
      comp.winning_bid_psf       !== "" ||
      comp.winning_bid_total     !== "" ||
      comp.winner_name?.trim()   !== "" ||
      comp.broker_debrief_notes?.trim() !== ""
    );
  }

  if (!open || !deal || !targetStage) return null;

  async function handleSave() {
    if (!reason) { setError("Pick a reason."); return; }
    setSaving(true); setError(null);

    // 1. Kill fields on the deal itself. Preserve everything else so the
    //    write doesn't wipe siblings — updateDeal reads the whole shape.
    const dealPayload = {
      ...deal,
      stage: targetStage,
      killedAt: new Date().toISOString(),
      killedReason: reason,
      killedNarrative: narrative.trim() || null,
    };
    const { data, error: dealErr } = await updateDeal(deal.id, dealPayload);
    if (dealErr) {
      setSaving(false);
      setError(dealErr.message);
      return;
    }

    // 2. Competition row — always write when there's content OR a prior row
    //    exists (so the user can edit or clear fields). Silently skipped when
    //    the section is untouched on a stage that never had competition data.
    if (competitionHasContent() || compExisted) {
      const result = await upsertCompetition(deal.id, {
        ...comp,
        lost_reason: reason,
      }, userName);
      if (result.error) {
        setSaving(false);
        setError(`Deal saved but competition detail failed: ${result.error}`);
        return;
      }
    }

    setSaving(false);
    onSaved?.({ updated: data });
  }

  const cls = darkMode
    ? { bg: "bg-[#242424]", panel: "bg-[#2c2c2c]", border: "border-[#3a3a3a]", text: "text-white", muted: "text-[#8a8a8a]", input: "bg-[#242424] border-[#3a3a3a] text-white placeholder-[#6a6a6a]" }
    : { bg: "bg-white",     panel: "bg-pp-light",  border: "border-pp-gray",  text: "text-pp-dark", muted: "text-[#8a8a8a]", input: "bg-white border-pp-gray text-pp-dark placeholder-[#7a7a7a]" };

  const inputCls = `w-full text-sm px-3 py-2 rounded-lg border ${cls.input} focus:outline-none focus:border-pp-red`;
  const labelCls = "block text-xs font-semibold text-[#8a8a8a] uppercase tracking-wider mb-1.5";

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-6" style={{ background: "rgba(0,0,0,0.55)" }} onClick={onCancel}>
      <div className={`${cls.bg} ${cls.text} w-full max-w-2xl max-h-[90vh] rounded-xl shadow-2xl flex flex-col font-montserrat`} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={`px-5 py-4 border-b ${cls.border} flex items-center justify-between flex-shrink-0`}>
          <div>
            <h2 className="text-lg font-bold">Mark deal as {targetStage}</h2>
            <p className={`text-xs ${cls.muted} mt-0.5`}>Capture the reason and any notes so we can learn from it later.</p>
          </div>
          <button onClick={onCancel} className={`text-xl ${cls.muted} hover:${cls.text} px-2`} aria-label="Close">✕</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-4">
          <div>
            <label className={labelCls}>Reason</label>
            <select value={reason} onChange={e => setReason(e.target.value)} className={inputCls}>
              <option value="">— pick one —</option>
              {reasonOptions.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>

          <div>
            <label className={labelCls}>Narrative</label>
            <textarea
              value={narrative}
              onChange={e => setNarrative(e.target.value)}
              rows={4}
              placeholder="What happened? What did we learn? Any relationship or timing context worth preserving."
              className={`${inputCls} resize-none`}
            />
          </div>

          <div className={`border-t ${cls.border} pt-4 mt-1`}>
            <h3 className="text-sm font-semibold mb-1">Competition detail <span className={`text-xs font-normal ${cls.muted}`}>— optional</span></h3>
            <p className={`text-xs ${cls.muted} mb-3`}>
              {targetStage === "Lost"
                ? "What did we bid, and who beat us?"
                : "If someone else bought this or it went to a bid we heard about, capture the detail here."}
            </p>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Est. Bidders</label>
                  <input type="number" value={comp.num_bidders_estimated}
                    onChange={e => setComp(c => ({ ...c, num_bidders_estimated: e.target.value }))}
                    className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Best & Final?</label>
                  <div className="flex gap-2">
                    {[
                      { v: true,  l: "Yes" },
                      { v: false, l: "No" },
                      { v: null,  l: "?" },
                    ].map(o => (
                      <button key={String(o.v)} type="button"
                        onClick={() => setComp(c => ({ ...c, made_best_and_final: o.v }))}
                        className={`flex-1 text-sm px-2 py-2 rounded-lg border ${comp.made_best_and_final === o.v
                          ? "bg-pp-red border-pp-red text-white"
                          : `${cls.input} hover:border-pp-red/60`}`}
                        style={comp.made_best_and_final === o.v ? { color: "#fff" } : {}}>
                        {o.l}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className={labelCls}>Our Final Bid ($/SF)</label>
                  <input type="number" step="0.01" value={comp.our_final_bid_psf}
                    onChange={e => setComp(c => ({ ...c, our_final_bid_psf: e.target.value }))}
                    className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Our Final Bid ($ total)</label>
                  <input type="number" value={comp.our_final_bid_total}
                    onChange={e => setComp(c => ({ ...c, our_final_bid_total: e.target.value }))}
                    className={inputCls} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className={labelCls}>Winning Bid ($/SF)</label>
                  <input type="number" step="0.01" value={comp.winning_bid_psf}
                    onChange={e => setComp(c => ({ ...c, winning_bid_psf: e.target.value }))}
                    className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Winning Bid ($ total)</label>
                  <input type="number" value={comp.winning_bid_total}
                    onChange={e => setComp(c => ({ ...c, winning_bid_total: e.target.value }))}
                    className={inputCls} />
                </div>
              </div>

              <div className="mt-3">
                <label className={labelCls}>Winner</label>
                <input value={comp.winner_name}
                  onChange={e => setComp(c => ({ ...c, winner_name: e.target.value }))}
                  placeholder="Buyer name (e.g. Prologis)"
                  className={inputCls} />
              </div>

              <div className="mt-3">
                <label className={labelCls}>Broker Debrief</label>
                <textarea value={comp.broker_debrief_notes}
                  onChange={e => setComp(c => ({ ...c, broker_debrief_notes: e.target.value }))}
                  rows={3} placeholder="What did the broker tell us about how the bid stack shook out?"
                  className={`${inputCls} resize-none`} />
              </div>
          </div>
        </div>

        {error && (
          <div className="px-5 pb-2 text-sm text-[#f04f44]">{error}</div>
        )}

        {/* Footer */}
        <div className={`px-5 py-3 border-t ${cls.border} flex items-center justify-end gap-2 flex-shrink-0`}>
          <button onClick={onCancel} disabled={saving} className={`text-sm px-4 py-2 rounded-lg border ${cls.border} ${cls.muted} hover:${cls.text} disabled:opacity-40`}>Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ color: "#ffffff" }}
            className="text-sm px-4 py-2 rounded-lg bg-pp-red hover:bg-pp-red-light font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? "Saving…" : `Mark as ${targetStage}`}
          </button>
        </div>
      </div>
    </div>
  );
}
